import CheckSvg from '@/assets/pricing/check.svg?react'
import MicrophoneSvg from '@/assets/pricing/microphone.svg?react'
import PricingAccessibilitySvg from '@/assets/pricing/pricingAccessibility.svg?react'
import heroBackgroundImg from '@/assets/pricing/pricingHeroBackground.png?minW=1024&picture-image-tools'
import heroBackgroundMobileImg from '@/assets/pricing/pricingHeroBackgroundMobile.png?minW=375&picture-image-tools'
import SmileCircleSvg from '@/assets/pricing/smile-circle.svg?react'
import TelephoneCallSvg from '@/assets/pricing/telephone-call.svg?react'
import Picture from '@/components/Picture'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import HeadTags from '@/lib/head/HeadTags'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './Pricing.module.scss'

const CALENDLY_URL = 'https://calendly.com/innosearch-patrick/' as const

const COBROWSE_FEATURES = [
  {
    icon: <MicrophoneSvg aria-hidden />,
    text: 'Visitors can get summaries, ask questions, open links, complete forms, and play videos - all by voice',
  },
  {
    icon: <SmileCircleSvg aria-hidden />,
    text: 'Zero friction',
  },
  {
    icon: <PricingAccessibilitySvg aria-hidden />,
    text: 'Richer, more accessible experiences from the first "hello"',
  },
] as const

const STOREFRONT_FEATURES = [
  {
    icon: <PricingAccessibilitySvg aria-hidden />,
    text: 'Built with top-tier accessibility and usability, it welcomes people with disabilities and seniors who many sites leave behind.',
  },
  {
    icon: <TelephoneCallSvg aria-hidden />,
    text: 'Plus, shoppers can buy by calling 1-855-SHOP-GPT, zero apps, zero screens, zero internet.',
  },
] as const

const STOREFRONT_PRICING_TIERS = [
  {
    name: 'Individual',
    price: '$149',
    period: '/ month + ',
    gmv: '8%',
    gmvText: ' of GMV',
    features: ['50 SKUs'],
  },
  {
    name: 'Startup',
    price: '$499',
    period: '/ month + ',
    gmv: '8%',
    gmvText: ' of GMV',
    features: ['500 SKUs'],
  },
  {
    name: 'Business',
    price: '$999',
    period: '/ month + ',
    gmv: '8%',
    gmvText: ' of GMV',
    features: ['5,000 SKUs'],
  },
  {
    name: 'Venture',
    price: '$4,999',
    period: '/ month + ',
    gmv: '8%',
    gmvText: ' of GMV',
    features: ['50,000 SKUs'],
  },
] as const

const PRICING_TIERS = [
  {
    name: 'Individual',
    price: '$149',
    period: '/ month',
    features: ['2,000 messages per month', '5 concurrent threads'],
  },
  {
    name: 'Startup',
    price: '$499',
    period: '/ month',
    features: ['8,000 messages per month', '20 concurrent threads'],
  },
  {
    name: 'Business',
    price: '$999',
    period: '/ month',
    features: ['25,000 messages per month', '50 concurrent threads'],
  },
  {
    name: 'Venture',
    price: '$4,999',
    period: '/ month',
    features: ['200,000 messages per month', '150 concurrent threads'],
  },
] as const

export default function Pricing() {
  return (
    <div className={style.Container}>
      <div className={style.Page}>
        <HeadTags title='Pricing' />
        <Navbar variant='dark' haveCart={false} />
        <main {...skipTargetProps('main-content')}>
          <VisuallyHidden as='h1'>InnoSearch AI Pricing Plans</VisuallyHidden>

          {/* Hero Section */}
          <section className={style.HeroSection} aria-labelledby='pricing-hero-heading'>
            <Picture
              src={{
                pic: heroBackgroundImg,
              }}
              sizes='(max-width: 1023px) 100vw, 90rem'
              fetchPriority='high'
              alt=''
              className={style.HeroBackgroundDesktop}
              preload
            />
            <Picture
              src={{
                pic: heroBackgroundMobileImg,
              }}
              sizes='(max-width: 1023px) 100vw, 375px'
              fetchPriority='high'
              alt=''
              className={style.HeroBackgroundMobile}
              preload
            />
            <h2 id='pricing-hero-heading' className={style.HeroMobileTitle}>
              Pricing Plans
            </h2>
          </section>

          {/* CoBrowse AI Section */}
          <section className={style.FeatureSection} aria-labelledby='cobrowse-heading'>
            <h2 id='cobrowse-heading' className={style.FeatureSectionTitle}>
              CoBrowse AI
            </h2>
            <p className={style.FeatureSectionDescription}>
              Add InnoSearch's plug-and-play voice plugin and turn every page into a conversation.
            </p>

            <VisuallyHidden>
              <p>CoBrowse AI includes the following features:</p>
            </VisuallyHidden>

            <div className={style.FeatureCards} role='list' aria-label='CoBrowse AI features'>
              {COBROWSE_FEATURES.map((feature, index) => (
                <div key={index} className={style.FeatureCard} role='listitem'>
                  <div className={style.FeatureCardIcon} aria-hidden='true'>
                    {feature.icon}
                  </div>
                  <div className={style.FeatureCardText}>{feature.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Pricing Cards Section */}
          <section className={style.PricingCardsSection} aria-labelledby='pricing-cards-heading'>
            <VisuallyHidden as='h3' id='pricing-cards-heading'>
              Pricing Plans
            </VisuallyHidden>

            <VisuallyHidden>
              <p>Choose from our CoBrowse AI pricing plans:</p>
            </VisuallyHidden>

            <div className={style.PricingCards} role='group' aria-label='CoBrowse AI pricing plans'>
              {PRICING_TIERS.map(tier => (
                <article
                  key={tier.name}
                  className={style.PricingCard}
                  aria-labelledby={`${tier.name.toLowerCase()}-plan-name`}
                >
                  <div className={style.PlanBadge}>
                    <h4 id={`${tier.name.toLowerCase()}-plan-name`} className={style.PlanName}>
                      {tier.name}
                    </h4>
                  </div>

                  <ul className={style.PricingDetails} aria-label={`${tier.name} plan details`}>
                    <li className={style.PriceContainer} aria-label={`${tier.price}${tier.period}`}>
                      <span className={style.Price} aria-hidden='true'>
                        {tier.price}
                      </span>
                      <span className={style.Period} aria-hidden='true'>
                        {tier.period}
                      </span>
                    </li>
                    {tier.features.map(feature => (
                      <li key={feature} className={style.Feature}>
                        <CheckSvg aria-hidden className={style.CheckIcon} />
                        <span className={style.FeatureText}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className={style.CardFooter}>
                    <a
                      href={CALENDLY_URL}
                      target='_blank'
                      rel='noopener noreferrer'
                      aria-label={`Start ${tier.name} plan for ${tier.price}${tier.period}`}
                      className={style.StartButton}
                    >
                      Start now
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* White Separator */}
          <div className={style.WhiteSeparator} aria-hidden='true' />

          {/* Storefront Section */}
          <section className={style.FeatureSection} aria-labelledby='storefront-heading'>
            <h2 id='storefront-heading' className={style.FeatureSectionTitle}>
              Storefront
            </h2>
            <p className={style.FeatureSectionDescription}>
              Open a dedicated InnoSearch Storefront and put your products in front of a wider, more
              inclusive audience.
            </p>

            <VisuallyHidden>
              <p>Storefront includes the following features:</p>
            </VisuallyHidden>

            <div className={style.FeatureCards} role='list' aria-label='Storefront features'>
              {STOREFRONT_FEATURES.map((feature, index) => (
                <div key={index} className={style.FeatureCard} role='listitem'>
                  <div className={style.FeatureCardIcon} aria-hidden='true'>
                    {feature.icon}
                  </div>
                  <div className={style.FeatureCardText}>{feature.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Storefront Pricing Cards Section */}
          <section
            className={style.PricingCardsSection}
            aria-labelledby='storefront-pricing-heading'
          >
            <VisuallyHidden as='h3' id='storefront-pricing-heading'>
              Storefront Pricing Plans
            </VisuallyHidden>

            <VisuallyHidden>
              <p>Choose from our Storefront pricing plans:</p>
            </VisuallyHidden>

            <div className={style.PricingCards} role='group' aria-label='Storefront pricing plans'>
              {STOREFRONT_PRICING_TIERS.map(tier => (
                <article
                  key={tier.name}
                  className={style.PricingCard}
                  aria-labelledby={`storefront-${tier.name.toLowerCase()}-plan-name`}
                >
                  <div className={style.PlanBadge}>
                    <h4
                      id={`storefront-${tier.name.toLowerCase()}-plan-name`}
                      className={style.PlanName}
                    >
                      {tier.name}
                    </h4>
                  </div>

                  <ul className={style.PricingDetails} aria-label={`${tier.name} plan details`}>
                    <li
                      className={style.PriceContainer}
                      aria-label={`${tier.price}${tier.period}${tier.gmv}${tier.gmvText}`}
                    >
                      <span className={style.Price} aria-hidden='true'>
                        {tier.price}
                      </span>
                      <span className={style.Period} aria-hidden='true'>
                        {tier.period}
                      </span>
                      <span className={style.Price} aria-hidden='true'>
                        {tier.gmv}
                      </span>
                      <span className={style.Period} aria-hidden='true'>
                        {tier.gmvText}
                      </span>
                    </li>
                    <li className={style.Feature}>
                      <CheckSvg aria-hidden className={style.CheckIcon} />
                      <span className={style.FeatureText}>{tier.features[0]}</span>
                    </li>
                  </ul>

                  <div className={style.CardFooter}>
                    <a
                      href={CALENDLY_URL}
                      target='_blank'
                      rel='noopener noreferrer'
                      aria-label={`Start ${tier.name} plan for ${tier.price}${tier.period}`}
                      className={style.StartButton}
                    >
                      Start now
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </div>
  )
}
